// Vocaler studio — application root: providers, router, shell.
import { lazy, Suspense, type ComponentType } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ToastProvider } from "./lib/ui";
import { AuthProvider, AuthGate } from "./lib/auth/AuthContext";
import { PlayerProvider } from "./lib/player/PlayerContext";
import { CreditsProvider } from "./lib/credits/CreditsContext";
import { AppShell, StubPage } from "./app/AppShell";
import { CommandPalette } from "./app/CommandPalette";
import { OperatorGate } from "./lib/auth/OperatorGate";
import { LocaleSync } from "./i18n/LocaleSync";
import { RouteFallback } from "./app/RouteFallback";

// Auth + shell stay in the entry chunk: they're on the critical path for
// first paint (logged-out users land on Login; the shell wraps every
// authed route). Everything below is split into its own chunk, fetched on
// navigation — so the initial bundle no longer carries RenderSheet, the
// visualizer, and the 9 operator-only platform pages most users never open.
import { LoginPage } from "./features/auth/LoginPage";

// ``lazyNamed`` wraps a named export for React.lazy (which expects a
// default). The import string stays a literal so Vite can statically
// split each into its own chunk.
function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  factory: () => Promise<M>,
  name: K,
): React.LazyExoticComponent<ComponentType<Record<string, never>>> {
  return lazy(() =>
    factory().then((m) => ({ default: m[name] as ComponentType<Record<string, never>> })),
  );
}

const SignupPage = lazyNamed(() => import("./features/auth/SignupPage"), "SignupPage");
const ResetPasswordPage = lazyNamed(() => import("./features/auth/ResetPasswordPage"), "ResetPasswordPage");
const ForgotPasswordPage = lazyNamed(() => import("./features/auth/ForgotPasswordPage"), "ForgotPasswordPage");
const OAuthCallbackPage = lazyNamed(() => import("./features/auth/OAuthCallbackPage"), "OAuthCallbackPage");
const OnboardingPage = lazyNamed(() => import("./features/onboarding/OnboardingPage"), "OnboardingPage");
const SharePage = lazyNamed(() => import("./features/share/SharePage"), "SharePage");

const HomePage = lazyNamed(() => import("./features/dashboard/HomePage"), "HomePage");
const GeneratePage = lazyNamed(() => import("./features/generate/GeneratePage"), "GeneratePage");
const LibraryPage = lazyNamed(() => import("./features/library/LibraryPage"), "LibraryPage");
const RendersPage = lazyNamed(() => import("./features/renders/RendersPage"), "RendersPage");
const ChannelsPage = lazyNamed(() => import("./features/channels/ChannelsPage"), "ChannelsPage");
const SettingsPage = lazyNamed(() => import("./features/settings/SettingsPage"), "SettingsPage");
const IntegrationsPage = lazyNamed(() => import("./features/integrations/IntegrationsPage"), "IntegrationsPage");
const TeamPage = lazyNamed(() => import("./features/team/TeamPage"), "TeamPage");

// Operator god-mode — split as its own group so the platform admin code
// never ships to normal users' bundles.
const PlatformDashboardPage = lazyNamed(() => import("./features/platform/PlatformDashboardPage"), "PlatformDashboardPage");
const PlatformTenantsPage = lazyNamed(() => import("./features/platform/PlatformTenantsPage"), "PlatformTenantsPage");
const PlatformTenantDetailPage = lazyNamed(() => import("./features/platform/PlatformTenantDetailPage"), "PlatformTenantDetailPage");
const PlatformUsersPage = lazyNamed(() => import("./features/platform/PlatformUsersPage"), "PlatformUsersPage");
const PlatformUserDetailPage = lazyNamed(() => import("./features/platform/PlatformUserDetailPage"), "PlatformUserDetailPage");
const PlatformJobsPage = lazyNamed(() => import("./features/platform/PlatformJobsPage"), "PlatformJobsPage");
const PlatformJobDetailPage = lazyNamed(() => import("./features/platform/PlatformJobDetailPage"), "PlatformJobDetailPage");
const PlatformAuditPage = lazyNamed(() => import("./features/platform/PlatformAuditPage"), "PlatformAuditPage");
const PlatformSettingsPage = lazyNamed(() => import("./features/platform/PlatformSettingsPage"), "PlatformSettingsPage");

/** /platform/<rest> -> /admin/<rest>, preserving query and hash. */
function PlatformRedirect() {
  const { pathname, search, hash } = useLocation();
  return <Navigate to={pathname.replace(/^\/platform/, "/admin") + search + hash} replace />;
}

export default function App() {
  return (
    <BrowserRouter basename={import.meta.env.BASE_URL}>
      <ToastProvider>
        <AuthProvider>
          <LocaleSync />
          {/* One Suspense boundary around the route table — any lazy route
              shows the lightweight fallback while its chunk loads. */}
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              {/* Public — no AuthGate. */}
              <Route path="login" element={<LoginPage />} />
              <Route path="signup" element={<SignupPage />} />
              <Route path="reset" element={<ResetPasswordPage />} />
              <Route path="forgot" element={<ForgotPasswordPage />} />
              <Route path="auth/callback" element={<OAuthCallbackPage />} />
              <Route path="onboarding" element={<OnboardingPage />} />
              {/* Public share landing page. Auth-optional — fetches public
                  metadata without login, gates the audio behind signup. */}
              <Route path="share/:id" element={<SharePage />} />
              {/* Everything below requires an authenticated session. */}
              <Route
                element={
                  <AuthGate>
                    <PlayerProvider>
                      <CreditsProvider>
                        <CommandPalette />
                        <AppShell />
                      </CreditsProvider>
                    </PlayerProvider>
                  </AuthGate>
                }
              >
                <Route index element={<HomePage />} />
                <Route path="generate" element={<GeneratePage />} />
                <Route path="library" element={<LibraryPage />} />
                <Route path="renders" element={<RendersPage />} />
                <Route path="channels" element={<ChannelsPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                {/* Designer prompt kept /api-keys as a redirect alias so old
                    bookmarks survive the rename. */}
                <Route path="api-keys" element={<Navigate to="/integrations" replace />} />
                <Route path="team" element={<TeamPage />} />
                {/* Platform-admin god-mode. OperatorGate redirects non-operators
                    to "/" — the sidebar already hides the group, this is
                    defense-in-depth for a direct URL paste. */}
                <Route path="admin" element={<OperatorGate><PlatformDashboardPage /></OperatorGate>} />
                <Route path="admin/tenants" element={<OperatorGate><PlatformTenantsPage /></OperatorGate>} />
                <Route path="admin/tenants/:tenantId" element={<OperatorGate><PlatformTenantDetailPage /></OperatorGate>} />
                <Route path="admin/users" element={<OperatorGate><PlatformUsersPage /></OperatorGate>} />
                <Route path="admin/users/:userId" element={<OperatorGate><PlatformUserDetailPage /></OperatorGate>} />
                <Route path="admin/jobs" element={<OperatorGate><PlatformJobsPage /></OperatorGate>} />
                <Route path="admin/jobs/:jobId" element={<OperatorGate><PlatformJobDetailPage /></OperatorGate>} />
                <Route path="admin/audit" element={<OperatorGate><PlatformAuditPage /></OperatorGate>} />
                <Route path="admin/settings" element={<OperatorGate><PlatformSettingsPage /></OperatorGate>} />
                {/* Renamed to /admin. Same pattern as api-keys and plan below,
                    but carrying the rest of the path so a deep link like
                    /platform/jobs/job_0abc still lands on that job. */}
                <Route path="platform" element={<Navigate to="/admin" replace />} />
                <Route path="platform/*" element={<PlatformRedirect />} />
                <Route path="publish" element={<StubPage name="Publish queue" />} />
                <Route path="cli" element={<StubPage name="CLI" />} />
                <Route path="mcp" element={<StubPage name="MCP" />} />
                <Route path="templates" element={<StubPage name="Templates" />} />
                <Route path="plan" element={<Navigate to="/settings" replace />} />
                <Route path="*" element={<StubPage name="Not found" />} />
              </Route>
            </Routes>
          </Suspense>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
